import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

// Constants
const RATE_PER_MINUTE = 0.43; // USD per minute (approximately 1.6 SAR)

// Utility functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const getMonthYearOptions = () => {
  const options = [];
  const currentDate = new Date();
  
  // Generate options for the last 12 months
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    options.push({ value, label });
  }
  
  return options;
};

// Components
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="p-4 md:p-6 2xl:p-10">
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" role="alert">
      <strong className="font-semibold">Error:</strong>
      <span className="block sm:inline ml-1">{message}</span>
    </div>
  </div>
);

const BillingPage = () => {
  const [billingData, setBillingData] = useState({
    totalCalls: 0,
    totalMinutes: 0,
    totalSeconds: 0,
    totalAmount: 0,
    calls: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [isLoadingAssistants, setIsLoadingAssistants] = useState(true);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [userRole, setUserRole] = useState(null);
  
  // Set default to current month
  const currentDate = new Date();
  const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Fetch assistants from Supabase
  useEffect(() => {
    const fetchAssistants = async () => {
      setIsLoadingAssistants(true);
      try {
        console.log('Fetching user...');
        // Get the current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error('Error getting user:', userError);
          throw userError;
        }
        if (!user) {
          console.error('No user found');
          setError('Not authenticated. Please log in.');
          return;
        }
        console.log('User found:', user.id);

        // First get the user's organization memberships
        console.log('Fetching organization memberships...');
        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_members')
          .select('organization_id, role')
          .eq('user_id', user.id);

        if (membershipsError) {
          console.error('Error fetching organization memberships:', membershipsError);
          throw membershipsError;
        }
        
        console.log('Organization memberships response:', memberships);
        
        if (!memberships || memberships.length === 0) {
          console.error('No organization memberships found for user');
          setError('No organization found. Please contact your administrator.');
          return;
        }

        // Check if user is admin
        const userMembership = memberships[0];
        setUserRole(userMembership.role);
        
        if (userMembership.role !== 'admin') {
          setError('Access denied. Billing page is only available for administrators.');
          return;
        }

        // Get all assistants for the organization
        console.log('Fetching assistants for organization:', memberships[0].organization_id);
        const { data: assistantsData, error: assistantsError } = await supabase
          .from('organization_assistants')
          .select(`
            assistant_id,
            organizations (
              name
            )
          `)
          .eq('organization_id', memberships[0].organization_id);

        if (assistantsError) {
          console.error('Error fetching assistants:', assistantsError);
          throw assistantsError;
        }

        console.log('Assistants response:', assistantsData);

        if (!assistantsData || assistantsData.length === 0) {
          console.error('No assistants found for organization');
          setError('No assistants found for your organization.');
          return;
        }

        // Map the assistants data
        const assistantsList = assistantsData.map(a => ({
          id: a.assistant_id,
          organizationName: a.organizations?.name || 'Unknown Organization'
        }));

        console.log('Processed assistants list:', assistantsList);
        setAssistants(assistantsList);
        if (assistantsList.length > 0) {
          setSelectedAssistant(assistantsList[0].id);
        }
      } catch (error) {
        console.error('Error in fetchAssistants:', error);
        setError('Failed to fetch assistants: ' + error.message);
      } finally {
        setIsLoadingAssistants(false);
      }
    };

    fetchAssistants();
  }, []);

  // Fetch billing data using optimized server-side functions
  useEffect(() => {
    const fetchBillingData = async () => {
      if (!selectedMonth || !selectedAssistant) {
        console.log('Missing required data:', { selectedMonth, selectedAssistant });
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        // Parse selected month
        const [year, month] = selectedMonth.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59, 999);

        console.log('Fetching billing data using optimized functions:', { 
          assistantId: selectedAssistant, 
          startDate: startDate.toISOString(), 
          endDate: endDate.toISOString() 
        });

        // Use optimized RPC function to get call statistics
        const { data: statsData, error: statsError } = await supabase
          .rpc('get_call_statistics', {
            p_assistant_id: selectedAssistant,
            p_start_date: startDate.toISOString(),
            p_end_date: endDate.toISOString()
          });

        if (statsError) {
          throw new Error('Failed to fetch call statistics: ' + statsError.message);
        }

        console.log('Statistics data received:', statsData);

        // Get the statistics from the first (and only) row
        const stats = statsData && statsData.length > 0 ? statsData[0] : {
          total_calls: 0,
          total_minutes: 0,
          successful_calls: 0,
          failed_calls: 0,
          missed_calls: 0
        };

        // Convert total_minutes to total_seconds and round up minutes for billing
        const totalSeconds = Math.round(stats.total_minutes * 60);
        const totalMinutes = Math.ceil(stats.total_minutes); // Round up to the next minute for billing
        const totalAmount = totalMinutes * RATE_PER_MINUTE;

        // Still fetch individual calls for the detailed list (but this is optional)
        // We could optimize this further by only fetching when needed
        const { data: callsData, error: callsError } = await supabase
          .from('calls')
          .select(`
            id,
            vapi_call_id,
            assistant_id,
            name,
            status,
            customer_number,
            customer_name,
            created_at_vapi,
            started_at_vapi,
            ended_at_vapi,
            ended_reason,
            cost,
            cost_breakdown,
            analysis
          `)
          .eq('assistant_id', selectedAssistant)
          .gte('created_at_vapi', startDate.toISOString())
          .lte('created_at_vapi', endDate.toISOString())
          .order('created_at_vapi', { ascending: false });

        if (callsError) {
          console.warn('Failed to fetch detailed calls data:', callsError.message);
          // Don't throw error, just use empty calls array
        }

        // Process calls for the detailed list
        const calls = (callsData || []).map(call => {
          // Calculate call duration in seconds
          let callDurationSeconds = 0;
          if (call.started_at_vapi && call.ended_at_vapi) {
            const start = new Date(call.started_at_vapi);
            const end = new Date(call.ended_at_vapi);
            if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start) {
              callDurationSeconds = Math.floor((end - start) / 1000);
            }
          }

          return {
            id: call.vapi_call_id,
            name: call.name,
            customerNumber: call.customer_number,
            customerName: call.customer_name,
            createdAt: call.created_at_vapi,
            duration: callDurationSeconds,
            status: call.status,
            endedReason: call.ended_reason,
            success: call.analysis?.successEvaluation === 'true'
          };
        });

        setBillingData({
          totalCalls: stats.total_calls,
          totalMinutes,
          totalSeconds,
          totalAmount,
          calls
        });

      } catch (e) {
        console.error("Failed to fetch billing data:", e);
        setError(e.message);
        setBillingData({
          totalCalls: 0,
          totalMinutes: 0,
          totalSeconds: 0,
          totalAmount: 0,
          calls: []
        });
      }
      setIsLoading(false);
    };

    fetchBillingData();
  }, [selectedMonth, selectedAssistant]);

  // Fetch invoices when month changes
  useEffect(() => {
    if (userRole === 'admin') {
      fetchInvoices();
    }
  }, [userRole]);

  const monthOptions = getMonthYearOptions();

  // Get current month in YYYY-MM format
  const getCurrentMonthYear = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  // Check if the selected month is the current month
  const isCurrentMonth = selectedMonth === getCurrentMonthYear();

  // Check if there's already an open or paid invoice for the selected month
  const hasExistingInvoice = invoices.some(invoice => {
    // Check if invoice metadata contains the selected month
    const invoiceMonth = invoice.metadata?.month;
    return invoiceMonth === selectedMonth && (invoice.status === 'open' || invoice.status === 'paid');
  });

  const fetchInvoices = async () => {
    setIsLoadingInvoices(true);
    try {
      // Get the current user for organization access
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get user's organization
      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_members')
        .select('organization_id, role')
        .eq('user_id', user.id);

      if (membershipsError) throw membershipsError;
      if (!memberships || memberships.length === 0) {
        throw new Error('No organization found');
      }

      // Check if user is admin
      if (memberships[0].role !== 'admin') {
        throw new Error('Access denied. Only administrators can view invoices.');
      }

      const organizationId = memberships[0].organization_id;

      // Get organization details
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', organizationId)
        .single();

      if (orgError || !organization) {
        throw new Error('Organization not found');
      }

      // Fetch invoices from Stripe for this organization
      const customerId = `org_${organizationId}`;
      const response = await fetch(`https://api.stripe.com/v1/invoices?customer=${customerId}&limit=100`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch invoices');
      }

      const data = await response.json();
      
      // Filter out void and deleted invoices
      const validInvoices = data.data.filter(invoice => 
        invoice.status !== 'void' && 
        invoice.status !== 'deleted' &&
        !invoice.deleted
      );
      
      setInvoices(validInvoices);

    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (billingData.totalAmount === 0) {
      alert('No billing amount to create invoice for.');
      return;
    }

    setIsCreatingInvoice(true);
    try {
      // Get the current user for organization access
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get user's organization
      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_members')
        .select('organization_id, role')
        .eq('user_id', user.id);

      if (membershipsError) throw membershipsError;
      if (!memberships || memberships.length === 0) {
        throw new Error('No organization found');
      }

      // Check if user is admin
      if (memberships[0].role !== 'admin') {
        throw new Error('Access denied. Only administrators can create invoices.');
      }

      const organizationId = memberships[0].organization_id;

      // Get organization details
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', organizationId)
        .single();

      if (orgError) {
        console.error('Error fetching organization:', orgError);
        throw new Error('Failed to fetch organization: ' + orgError.message);
      }

      if (!organization) {
        throw new Error('Organization not found');
      }

      // Use organization ID as the Stripe customer identifier
      // This creates a consistent customer ID based on the organization
      const stripeCustomerId = `org_${organizationId}`;

      // Check if customer exists in Stripe
      let customerExists = false;
      try {
        const existingCustomerResponse = await fetch(`https://api.stripe.com/v1/customers/${stripeCustomerId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
          }
        });
        customerExists = existingCustomerResponse.ok;
      } catch (error) {
        // Customer doesn't exist, we'll create it
        customerExists = false;
      }

      // If customer doesn't exist, create it
      if (!customerExists) {
        const customerResponse = await fetch('https://api.stripe.com/v1/customers', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            'id': stripeCustomerId, // Use org ID as customer ID
            'email': user.email,
            'name': organization.name,
            'metadata[organization_id]': organizationId,
            'metadata[supabase_user_id]': user.id,
            'metadata[source]': 'sooot_landing',
          })
        });

        if (!customerResponse.ok) {
          const errorData = await customerResponse.json();
          throw new Error(`Failed to create Stripe customer: ${errorData.error?.message || 'Unknown error'}`);
        }
      }

      // Create invoice
      const invoiceResponse = await fetch('https://api.stripe.com/v1/invoices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          'customer': stripeCustomerId,
          'description': `Voice AI Service for ${selectedMonth}`,
          'metadata[organization_id]': organizationId,
          'metadata[month]': selectedMonth,
          'metadata[total_calls]': billingData.totalCalls.toString(),
          'metadata[total_minutes]': billingData.totalMinutes.toString(),
          'metadata[assistant_id]': selectedAssistant,
          'collection_method': 'send_invoice',
          'days_until_due': '30'
        })
      });

      if (!invoiceResponse.ok) {
        const errorData = await invoiceResponse.json();
        throw new Error(errorData.error?.message || 'Failed to create invoice');
      }

      const invoice = await invoiceResponse.json();

      // Add invoice item
      const itemResponse = await fetch('https://api.stripe.com/v1/invoiceitems', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          'customer': stripeCustomerId,
          'invoice': invoice.id,
          'amount': Math.round(billingData.totalAmount * 100), // Convert to cents
          'currency': 'usd',
          'description': `Voice AI Service - ${selectedMonth}`
        })
      });

      if (!itemResponse.ok) {
        const errorData = await itemResponse.json();
        throw new Error(errorData.error?.message || 'Failed to add invoice item');
      }

      // Finalize the invoice
      const finalizeResponse = await fetch(`https://api.stripe.com/v1/invoices/${invoice.id}/finalize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
        }
      });

      if (!finalizeResponse.ok) {
        const errorData = await finalizeResponse.json();
        throw new Error(errorData.error?.message || 'Failed to finalize invoice');
      }

      const finalizedInvoice = await finalizeResponse.json();
      
      // Open the invoice in a new tab
      if (finalizedInvoice.hosted_invoice_url) {
        window.open(finalizedInvoice.hosted_invoice_url, '_blank');
      } else {
        alert(`Invoice created successfully! Invoice ID: ${invoice.id}`);
      }

    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Failed to create invoice: ' + error.message);
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  if (isLoadingAssistants) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!assistants.length) return <ErrorMessage message="No assistants available." />;

  return (
    <div className="p-4 md:p-6 2xl:p-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 sm:mb-0">
          Billing
        </h1>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark text-gray-700 dark:text-gray-300"
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          {assistants.length > 1 && (
            <select
              value={selectedAssistant || ''}
              onChange={(e) => setSelectedAssistant(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark text-gray-700 dark:text-gray-300"
            >
              {assistants.map((assistant) => (
                <option key={assistant.id} value={assistant.id}>
                  {assistant.organizationName} - Assistant {assistant.id.substring(0, 8)}...
                </option>
              ))}
            </select>
          )}

          {/* Create Invoice Button */}
          {billingData.totalCalls > 0 && (
            <button
              onClick={handleCreateInvoice}
              disabled={isCreatingInvoice || isCurrentMonth || hasExistingInvoice}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#6C63FF] hover:bg-[#5A52E5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6C63FF] disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                isCurrentMonth 
                  ? "Cannot create invoice for current month until it ends" 
                  : hasExistingInvoice 
                    ? "Invoice already exists for this month" 
                    : ""
              }
            >
              {isCreatingInvoice ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Create Invoice
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Billing Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white dark:bg-boxdark shadow-md rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Calls</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{billingData.totalCalls}</p>
                </div>
              </div>
            </div>



            <div className="bg-white dark:bg-boxdark shadow-md rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Billable Minutes</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{billingData.totalMinutes}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-boxdark shadow-md rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Amount</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(billingData.totalAmount)}</p>
                </div>
              </div>
            </div>
          </div>



          {/* Invoices List */}
          <div className="mt-8 bg-white dark:bg-boxdark shadow-md rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Invoices</h2>
              <button
                onClick={fetchInvoices}
                disabled={isLoadingInvoices}
                className="text-sm text-[#6C63FF] hover:text-[#5A52E5] disabled:opacity-50"
              >
                {isLoadingInvoices ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            
            {isLoadingInvoices ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#6C63FF]"></div>
              </div>
            ) : invoices.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No invoices found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Invoice Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Month</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-boxdark divide-y divide-gray-200 dark:divide-gray-700">
                    {invoices.map((invoice) => {
                      // Format service month from YYYY-MM to "Aug 2025" format
                      const formatServiceMonth = (monthString) => {
                        if (!monthString || monthString === 'N/A') return 'N/A';
                        const [year, month] = monthString.split('-');
                        const date = new Date(year, month - 1); // month is 0-based
                        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                      };
                      
                      const serviceMonth = formatServiceMonth(invoice.metadata?.month);
                      const amount = (invoice.amount_paid || invoice.amount_due) / 100; // Convert from cents
                      
                      const getStatusColor = (status) => {
                        switch (status) {
                          case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
                          case 'open': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
                          case 'draft': return 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200';
                          case 'void': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
                          default: return 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200';
                        }
                      };

                      return (
                        <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {invoice.number || invoice.id.substring(0, 20) + '...'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {serviceMonth}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {formatCurrency(amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex space-x-3">
                              {invoice.hosted_invoice_url && (
                                <button
                                  onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                                  className="text-[#6C63FF] hover:text-[#5A52E5]"
                                  title="View Invoice"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                              )}
                              {invoice.invoice_pdf && (
                                <button
                                  onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                  title="Download PDF"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </>
      )}
    </div>
  );
};

export default BillingPage;
